/**
 * The AUTH_CONTROL switch that can temporarily open the site to anonymous
 * visitors.
 *
 * When AUTH_CONTROL contains a row { ControlName: 'RemoveLogin', IsActive: true }
 * the site skips the sign-in gate: /login redirects to the home page and a
 * failed user lookup no longer bounces the visitor out.
 *
 * Two rules govern everything here:
 *
 *   1. FAIL CLOSED. Any outcome other than an explicit `removeLogin: true` —
 *      network error, timeout, 401, malformed body — leaves authentication ON.
 *      Getting this backwards would mean a backend hiccup silently unlocks the
 *      whole site.
 *
 *   2. RESOLVE BEFORE THE GATE RUNS. The flag has to be known before the
 *      redirect decision is made, so it is fetched during boot rather than
 *      lazily. It runs in parallel with the user lookup, so it costs no extra
 *      wall-clock time on a normal signed-in load.
 */

import axios from 'axios';
import { apiBase } from '@/store';

// Bounded so a hanging backend cannot hold the whole app on the loader. If the
// flag has not arrived by then, authentication stays on.
const TIMEOUT_MS = 6000;

let resolvedValue = null;   // null = not determined yet
let inflight = null;

/**
 * Synchronous read for code that runs after loadAuthControl() has settled —
 * route guards and component mounts. Before that it reports false, which is
 * the safe answer.
 */
export function isLoginRemoved() {
   return resolvedValue === true;
}

/** True once the lookup has finished, whichever way it went. */
export function isAuthControlResolved() {
   return resolvedValue !== null;
}

/**
 * Fetches the switch. Memoised: concurrent callers share one request and later
 * callers get the settled value.
 *
 * @returns {Promise<boolean>} true only when login is explicitly disabled
 */
export function loadAuthControl() {
   if (resolvedValue !== null) return Promise.resolve(resolvedValue);
   if (inflight) return inflight;

   inflight = axios
      // MEASiteBackendServerPublic, NOT MEASiteBackendServer.
      //
      // The main function is behind Catalyst's permission layer and answers
      // 401 NO_ACCESS to anonymous callers, so asking it whether login is
      // required could only ever be answered for people who had already
      // logged in. This switch has to be readable before the visitor signs
      // in, so it lives on the public function instead.
      .get(`${apiBase()}/server/MEASiteBackendServerPublic/api/authcontrol`, {
         timeout: TIMEOUT_MS,
         // The switch must reflect the table right now. Without this a browser
         // or CDN could keep serving "open" after it had been switched back.
         headers: { 'Cache-Control': 'no-cache' }
      })
      .then((response) => {
         // Strict equality on purpose: only a real boolean true opens the site.
         // A body of {} or { removeLogin: "false" } must not.
         resolvedValue = response?.data?.removeLogin === true;
         return resolvedValue;
      })
      .catch((error) => {
         // Reached on a network failure, on a timeout, or on a 401 if the
         // public function has not been deployed yet or is not in fact
         // public. Every one of those is indistinguishable from "the flag is
         // off", and every one of them must keep the login page in place.
         console.warn('AUTH_CONTROL lookup failed; authentication stays on.', error?.message || error);
         resolvedValue = false;
         return resolvedValue;
      })
      .finally(() => {
         inflight = null;
      });

   return inflight;
}

/** Drops the cached value so the next call re-reads the table. */
export function resetAuthControl() {
   resolvedValue = null;
   inflight = null;
}
